
import React from 'react';
import { Circle, Text } from 'react-konva';
import { NUM_VERTICES, LINE_SIZE, APPROX_TOLERANCE } from './Rhombus.js';
import { Globals } from './Globals';
var Point = require('./Point');

class RhombusList extends React.Component {

  state = {
    rhombus_list : null,
  }
  external_vertices : null;


  constructor( props) {
    super(props);
    this.state.rhombus_list = [];
    this.external_vertices = [];
  }

  assert( expression, text) {
    if (!expression) {
      throw new Error(text);
    }
  }

  static are_common_vertices_adjacent(common_vertices, max_vertices) {
    let cur = common_vertices[0];
    for (let i=1; i<common_vertices.length; i++) {
      if ((cur + 1) % max_vertices !== common_vertices[i]) {
        return false;
      }
      cur = common_vertices[i];
    }
    return true;
  }

  are_external_vertices_adjacent(rhombus) {

    if (this.is_empty()) {
      // degenerative case
      return true;
    }

    // get a list of the external vertices in the rhombus
    let rExVs = [];
    let rVs = rhombus.get_vertices();
    for(let i=0; i<this.external_vertices.length; i++) {
      for( let k=0; k<rVs.length; k++) {
        if (this.external_vertices[i].clone().subtract(rVs[k]).getLength() < APPROX_TOLERANCE) {
          rExVs.push(i);
        }
      }
    }

    // have we wrapped?
    rExVs.sort((a,b)=>a-b);
    if ((rExVs[0] === 0) && (rExVs[rExVs.length-1] === (this.external_vertices.length-1))) {
      // determine how many vertices are at the low-end
      let count=0;
      while( rExVs[count] == count) {
        count++;
      }
      // move these to the high-end to put in order
      for( let i=0; i<count; i++) {
        let v = rExVs.shift();
        rExVs.push(v);
      }
    }

    // the list is now sorted.
    let rc = RhombusList.are_common_vertices_adjacent( rExVs, this.external_vertices.length);
    return rc;
    
  }

  reset() {
    this.state.rhombus_list = [];
    this.external_vertices = [];
  }

  get_next_growth_vertex( max_x, max_y) {
    let next_vertex_idx = -1;
    
    // CLOSEST_TO_CENTER
    let globals = new Globals();
    switch (globals.next_node_criteria) {
      case Globals.NN_CLOSEST_TO_CENTER:
        // find the closest vertex to the first vertex created
        let vid = this.state.rhombus_list[0].get_vid();
        let start_v = this.state.rhombus_list[0].get_vertex(vid);
        // pick a big number
        let min_distance = max_x + max_y;
        for (let i=0; i<this.external_vertices.length; i++) {
          let ev = this.external_vertices[i];
          if ((ev.x <= max_x) && (ev.x >= 0) && (ev.y <= max_y) && (ev.y >= 0)) {
            let d = start_v.getDistance(ev);
            if (min_distance > d) {
              next_vertex_idx = i;
              min_distance = d;
            }
          }
        }
        break;
      case Globals.NN_RANDOM_INTERIOR:
        // make a list of interior angle indexes from external_vertices
        let list_of_interior_angles = { 36:[], 72:[], 108:[], 144:[] };
        let interior_angle_count = 0;
        let numev = this.external_vertices.length;
        for( let i=0; i<numev; i++) {
          let ev = this.external_vertices[i];
          if ((ev.x <= max_x) && (ev.x >= 0) && (ev.y <= max_y) && (ev.y >= 0)) {
            let prev = this.external_vertices[(i-1+numev) % numev];
            let cur = this.external_vertices[i];
            let next = this.external_vertices[(i+1+numev) % numev];
            let vl = prev.clone().subtract(cur);
            if (vl.cross(cur.clone().subtract(next)) < 0) {
              // this is an interior angle
              let angle = Math.floor((vl.getAngle(next.clone().subtract(cur)) + 5) / 36) * 36;
              if (angle < 180) {
                this.assert( angle in list_of_interior_angles, `bad angle of ${angle} at ${i}`);
                list_of_interior_angles[angle].push(i);
                interior_angle_count += 1;
              }
            }
          }
        }

        if (interior_angle_count > 0) {
          // pick the most acut angle that has members
          let most_acute_angle = -1;
          for( let a in list_of_interior_angles) {
            // iterate through keys in order of declaration (smallest to largest)
            if (list_of_interior_angles[a].length > 0) {
              most_acute_angle = a;
              break;
            }
          }
          this.assert( most_acute_angle > 0, `unexpected acute angle: ${most_acute_angle}`);

          // randomly pick one of the indexes in this list
          let al = list_of_interior_angles[most_acute_angle];
          next_vertex_idx = al[(Math.random() * al.length) | 0];
          break; // exit the switch statement
        }
        // we get here because no interior angles exist, fall through and pick
        // a random vertex

      case Globals.NN_RANDOM:
        if (next_vertex_idx < 0) {
          // make a list of the viewable vertices
          let viewable_vertices = [];
          for( let i=0; i<this.external_vertices.length; i++) {
            let ev = this.external_vertices[i];
            if ((ev.x <= max_x) && (ev.x >= 0) && (ev.y <= max_y) && (ev.y >= 0)) {
              viewable_vertices.push(i);
            }
          }
          if (viewable_vertices.length > 0) {
            next_vertex_idx =  viewable_vertices[(Math.random() * viewable_vertices.length) | 0];
          }
        }
        break;
      default:
        throw `Unknown next_node_criteria: ${globals.next_node_criteria}`;
    }

    if (next_vertex_idx < 0) {
      return null;
    }
    return this.external_vertices[next_vertex_idx];
  }

  is_empty() {
    return (this.state.rhombus_list.length === 0);
  }

  add( rhombus) {
    this.state.rhombus_list.push(rhombus);

    // update external_vertices list
    if (this.state.rhombus_list.length === 1) {
      this.external_vertices = this.external_vertices.concat(rhombus.get_vertices());
    } else {
      // common_vertices is a list of indexes in external_vertices in
      // common with the new rhombus. If there are 3 in this list, the middle
      // one should be removed
      let common_vertices = []
      // the non_common_vertex_ids is a list of new rhombus vertex_ids to be inserted
      // into the external_vertices. The insertion point is the last index contained
      // in common_vertices.
      let non_common_vertex_ids = []
      for (let i=0; i<this.external_vertices.length; i++) {
        let vid = rhombus.find_vertex_id(this.external_vertices[i]);
        if (vid >= 0) {
          common_vertices.push(i);
          if (non_common_vertex_ids.length === 0) {
            let start = Math.floor(vid / NUM_VERTICES)*NUM_VERTICES;
            for (let k=0; k<NUM_VERTICES; k++) {
              non_common_vertex_ids.push(start+k);
            }
          }
          let ncvid = -1;
          for( let k=0; k<non_common_vertex_ids.length; k++) {
            if (non_common_vertex_ids[k] === vid) {
              ncvid = k;
              break;
            }
          }
          this.assert(ncvid >= 0, `vid=${vid} not found in ${non_common_vertex_ids}`);
          non_common_vertex_ids.splice(ncvid,1);
        }
      }

      this.assert( [2,3,4].includes(common_vertices.length), 
                    `Invalid number (${common_vertices.length}) of common vertices in ${common_vertices}`);
      this.assert( [0,1,2].includes(non_common_vertex_ids.length),
                    `Invalid number of non-common vertices in ${non_common_vertex_ids}`);

      let external_insertion_idx = common_vertices[common_vertices.length-1];
      if (common_vertices.length >= 3) {
        // check if we've wrapped around, the vertices should be in clockwise order
        let i = 0;
        while ( !RhombusList.are_common_vertices_adjacent(common_vertices,this.external_vertices.length) ) {
          let v = common_vertices.shift();
          common_vertices.push(v);
          external_insertion_idx = 1;
          i += 1;
          this.assert(i < 4, "Infinite loop");
        }

        // keep the end vertices within common_vertices and remove the
        // 1 or 2 vertices in between. The vertices within common_vertices
        // are indexes into external_vertices, so be careful to remove
        // the higher index first
        if (common_vertices.length === 3) {
          this.external_vertices.splice(common_vertices[1],1);
          external_insertion_idx -= 1;
        } else { // common_vertices.length === 4
          this.external_vertices.splice(Math.max(common_vertices[1],common_vertices[2]),1);
          this.external_vertices.splice(Math.min(common_vertices[1],common_vertices[2]),1);
          external_insertion_idx -= 2;
        }
      }

      if (non_common_vertex_ids.length === 2) {
        if ((common_vertices[0] === 0) && (common_vertices[1] === (this.external_vertices.length-1))) {
          // we've wrapped so place the insertion point after the last external_vertices
          external_insertion_idx = this.external_vertices.length;
        }
        // swap the vertex_ids if they wrap around
        if (non_common_vertex_ids[1] - non_common_vertex_ids[0] > 1) {
          let t = non_common_vertex_ids[0];
          non_common_vertex_ids[0] = non_common_vertex_ids[1];
          non_common_vertex_ids[1] = t;
        }
      }

      // add non-common vertex from new rhombus
      for (let i=0; i<non_common_vertex_ids.length; i++) {
        this.external_vertices.splice( external_insertion_idx, 0, rhombus.get_vertex(non_common_vertex_ids[i]));
        external_insertion_idx += 1;
      }

    }
  }

  is_vertex_blocked( vertex, pos) {
    let vp = Point.fromObject(vertex).add(pos);
    for (let r of this.state.rhombus_list) {
      if (r.vertex_crosses_through( vertex, pos)) {
        return true;
      } 
      if (r.find_vertex_id(vp) >= 0) {
        // this vertex matches some point in the rhombus
        // only return true if this is NOT external
        for (let ev of this.external_vertices) {
          if (vp.getDistance(ev) <= APPROX_TOLERANCE) {
            return false;
          }
        }
        return true;
      }
    }
    return false;
  }

  does_rhombus_overlap(rhombus) {
    for (let r of this.state.rhombus_list) {
      if (rhombus.overlaps(r)) {
        return true;
      }
    }
    return false;
  }

  are_external_vertices_in_order() {
    this.assert( this.external_vertices.length > 1, "Need to add some rhombuses first");

    // assumption is that each vertex should be LINE_SIZE away from the
    // previous. If not, it's probably in the wrong order.
    for (let i=1; i<this.external_vertices.length; i++) {
      let length = this.external_vertices[i-1].getDistance(this.external_vertices[i]);
      if (Math.abs(LINE_SIZE - length) > APPROX_TOLERANCE) {
        return false;
      }
    }
    return true;
  }

  is_external_vertex(vertex) {
    for(let i=0; i<this.external_vertices.length; i++) {
      if (this.external_vertices[i].clone().subtract(vertex).getLength() < APPROX_TOLERANCE) {
        return true;
      }
    }
    return false;
  }

  get_external_vertex_number(vertex) {
    for(let i=0; i<this.external_vertices.length; i++) {
      if (this.external_vertices[i].clone().subtract(vertex).getLength() < APPROX_TOLERANCE) {
        return i;
      }
    }
    return -1;
  }

  get_size() {
    return this.state.rhombus_list.length;
  }

  place_start_of_external_vertices_at(vertex) {
    for(let i=0; i<this.external_vertices.length; i++) {
      if (this.external_vertices[0].clone().subtract(vertex).getLength() < APPROX_TOLERANCE) {
        // the supplied vertex and the current one are approximately the same length,
        // assume they are the same
        return;
      }
      let ev = this.external_vertices.shift();
      this.external_vertices.push(ev);
    }
    this.assert(false,`should not get here with vertex=${vertex}`);
  }

  render() {
    const { rhombus_list } = this.state;

    return (
      <>
        { rhombus_list.map((it,index) => it.render(index)) }
        { Globals.DEBUG &&
          this.external_vertices.map((ev,idx) => {
            return (
              <React.Fragment key={"f"+idx}>
                <Circle key={"c"+idx} x={ev.x} y={ev.y} radius={10} fill="green"/>
                <Text key={"t"+idx} text={idx} x={ev.x-2} y={ev.y-3} fontSize={8} strokeWidth={1} stroke="white"/>
              </React.Fragment>
            )
        })}
      </>
      )
  }
}

export { RhombusList };
